import { usePublishedData } from "../lib/published-data";
import { createFileRoute, notFound, redirect, useNavigate } from "@tanstack/react-router";
import merchStyles from "../styles/merch.css?url";
import shopStyles from "../styles/shop.css?url";
import { DocumentContent } from "../components/document-content";
import { ShopListing } from "../components/shop/shop-listing";
import { useShell } from "../lib/document-route";
import { creatorPageHead, shellFrom, withStylesheet } from "../lib/head";
import { nativeFeatureQuery } from "../lib/links";
import { searchParams } from "../lib/search";
import { loadShop } from "../server/shop";

type Search = { category?: string; q?: string; sort?: string; preview?: string };

const text = (value: unknown) => (typeof value === "string" && value ? value : undefined);

/**
 * Merch (/merch): the page the creator composed, or the native listing. A category or a search
 * always lists natively; search and sort narrow the listing in the browser.
 */
export const Route = createFileRoute("/merch/")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    category: text(search.category),
    // An empty search (`?q=`, the header's search button) still opens the listing and its field.
    q: search.q === undefined || search.q === null ? undefined : String(search.q).slice(0, 80),
    sort: text(search.sort),
    preview: search.preview === "1" ? "1" : undefined,
  }),
  // Only moving between the composed page and the listing asks for new data.
  loaderDeps: ({ search }) => ({ native: nativeFeatureQuery("/merch", searchParams(search)) }),
  loader: async ({ deps }) => {
    const data = await loadShop({ data: deps.native });
    if (data.kind === "missing") throw notFound();
    if (data.kind === "redirect") throw redirect({ href: data.to, statusCode: 302 });
    return data;
  },
  head: ({ matches, loaderData }) => {
    const shell = shellFrom(matches);
    if (!shell || !loaderData) return {};
    if (loaderData.kind === "page")
      return creatorPageHead(shell, {
        pathname: "/merch",
        title: loaderData.page.seoTitle || loaderData.page.title,
        description: loaderData.page.description,
        noindex: loaderData.private || loaderData.locked || loaderData.page.hideFromSearch,
        preloadImage: loaderData.lead,
      });
    const head = creatorPageHead(shell, {
      pathname: "/merch",
      title: `${loaderData.title} | ${loaderData.creatorName}`,
      description: loaderData.intro || `Shop ${loaderData.creatorName}'s collection.`,
    });
    // The listing's own styles come before the shop's, as the Astro page imported them.
    return withStylesheet(withStylesheet(head, merchStyles), shopStyles);
  },
  component: Merch,
});

function Merch() {
  const initial = Route.useLoaderData();
  const search = Route.useSearch();
  const shell = useShell();
  const { data } = usePublishedData(initial, shell, "/merch", "page");
  const navigate = useNavigate({ from: "/merch/" });
  if (data.kind === "page") return <DocumentContent data={data} shell={shell} />;
  return (
    <ShopListing
      shop={data}
      search={search}
      onSearch={({ q, sort }) =>
        void navigate({
          search: {
            ...search,
            q: q.trim().slice(0, 80) || undefined,
            sort: sort === "featured" ? undefined : sort,
          },
          resetScroll: false,
        })
      }
    />
  );
}
